import React, { useState, useEffect } from "react";
import DataTable from "react-data-table-component";
import Swal from "sweetalert2";
import toast from "react-hot-toast";
import HomeService from "../Services/HomeService";

const headLineStyle = {
  textAlign: "center",
  fontSize: "30px",
  color: "#868e96",
  margin: "15px",
  fontWeight: "bold",
};

function ManageFaq() {
  const [faqs, setFaqs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [itemID, setItemID] = useState("");
  const [isEdit, setIsEdit] = useState(false);
  const [editData, setEditData] = useState({
    question: "",
    answer: "",
  });

  // Function to show answer in popup
  const showAnswerPopup = (question, answer, title = "FAQ Details") => {
    Swal.fire({
      title: title,
      html: `
        <div style="text-align: left; max-height: 400px; overflow-y: auto; padding: 10px;">
          <h6 style="color: #333; margin-bottom: 10px;"><strong>Question:</strong></h6>
          <p style="white-space: pre-wrap; word-wrap: break-word; margin-bottom: 20px; padding: 10px; background: #f8f9fa; border-radius: 5px;">
            ${question || "No question available"}
          </p>
          <h6 style="color: #333; margin-bottom: 10px;"><strong>Answer:</strong></h6>
          <p style="white-space: pre-wrap; word-wrap: break-word; padding: 10px; background: #f8f9fa; border-radius: 5px;">
            ${answer || "No answer available"}
          </p>
        </div>
      `,
      showCloseButton: true,
      showConfirmButton: false,
      width: "700px",
      customClass: {
        popup: "faq-popup",
      },
    });
  };

  const columns = [
    {
      name: "S.No.",
      selector: (row, index) => index + 1,
      sortable: true,
    },
    {
      name: "Question",
      cell: (row) => {
        const question = row.question || "";
        const shortQuestion =
          question.length > 20 ? `${question.substring(0, 20)}...` : question;

        return (
          <div className="d-flex align-items-center">
            <span className="me-2" style={{ minWidth: "50px" }}>
              {shortQuestion || <span className="text-muted">No question</span>}
            </span>
            {question.length > 20 && (
              <button
                type="button"
                className="btn btn-sm p-0"
                onClick={() =>
                  showAnswerPopup(row.question, row.answer, "FAQ Details")
                }
                title="View full question and answer"
                style={{
                  background: "transparent",
                  border: "none",
                  color: "#007bff",
                  fontSize: "16px",
                }}
              >
                <i className="fa-solid fa-eye"></i>
              </button>
            )}
          </div>
        );
      },
      sortable: true,
    },
    {
      name: "Answer",
      cell: (row) => {
        const answer = row.answer || "";
        const shortAnswer =
          answer.length > 25 ? `${answer.substring(0, 25)}...` : answer;

        return (
          <div className="d-flex align-items-center">
            <span className="me-2" style={{ minWidth: "50px" }}>
              {shortAnswer || <span className="text-muted">No answer</span>}
            </span>
            {answer.length > 25 && (
              <button
                type="button"
                className="btn btn-sm p-0"
                onClick={() =>
                  showAnswerPopup(row.question, row.answer, "FAQ Details")
                }
                title="View full question and answer"
                style={{
                  background: "transparent",
                  border: "none",
                  color: "#007bff",
                  fontSize: "16px",
                }}
              >
                <i className="fa-solid fa-eye"></i>
              </button>
            )}
          </div>
        );
      },
      sortable: true,
    },
    {
      name: "Created Date",
      selector: (row) =>
        new Date(
          row.createdAt || row.createdOn || row.updatedAt
        ).toLocaleDateString(),
      sortable: true,
      center: true,
    },
    {
      name: "Action",
      cell: (row) => (
        <div>
          <button
            title="Edit"
            onClick={() => handleEdit(row)}
            className="btn btn-sm me-2"
            style={{
              background: "transparent",
              border: "none",
              color: "rgb(133, 218, 79)",
            }}
          >
            <i className="fa-solid fa-pen-to-square fs-5"></i>
          </button>
          <button
            title="Delete"
            onClick={() => handleDelete(row._id)}
            className="btn btn-sm"
            style={{
              background: "transparent",
              border: "none",
              color: "#dc3545",
            }}
          >
            <i className="fa-solid fa-trash-can fs-5"></i>
          </button>
        </div>
      ),
      center: true,
    },
  ];

  // Fetch all FAQs
  const fetchFaqs = async () => {
    setLoading(true);
    try {
      const res = await HomeService.getFaq();
      console.log("FAQs data:", res.data);
      setFaqs(res.data);
    } catch (error) {
      console.error("Error fetching FAQs:", error);
      toast.error("Failed to load FAQs!");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFaqs();
  }, []);

  // Handle edit button click
  const handleEdit = (row) => {
    setIsEdit(true);
    setEditData({
      question: row.question || "",
      answer: row.answer || "",
    });
    setItemID(row._id);
  };

  // Handle delete button click with SweetAlert confirmation
  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: "Delete FAQ?",
      text: "Are you sure you want to delete this FAQ?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete it!",
      confirmButtonColor: "#d33",
      cancelButtonText: "Cancel",
      reverseButtons: true,
    });

    if (result.isConfirmed) {
      try {
        const res = await HomeService.deleteFaq(id);
        if (res?.status) {
          Swal.fire("Deleted!", "FAQ has been deleted.", "success");
          fetchFaqs();
        } else {
          toast.error(res?.message || "Failed to delete FAQ");
        }
      } catch (error) {
        Swal.fire("Error!", "Failed to delete FAQ.", "error");
      }
    }
  };

  // Handle form submission for update
  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (!editData.question.trim()) {
      toast.error("Please enter a question.");
      return;
    }

    if (!editData.answer.trim()) {
      toast.error("Please enter an answer.");
      return;
    }

    try {
      // Prepare update data
      const data = {
        question: editData.question.trim(),
        answer: editData.answer.trim(),
      };

      console.log("Updating FAQ with data:", data);

      const res = await HomeService.updateFaq(itemID, data);

      if (res?.status) {
        toast.success("FAQ updated successfully!");
        setIsEdit(false);
        setEditData({ question: "", answer: "" });
        fetchFaqs();
      } else {
        toast.error(res?.message || "Failed to update FAQ.");
      }
    } catch (error) {
      toast.error("Failed to update FAQ.");
      console.error("Error updating FAQ:", error);
    }
  };

  // Handle cancel edit
  const handleCancel = () => {
    setIsEdit(false);
    setEditData({ question: "", answer: "" });
  };

  return (
    <>
      <div className="main_wrap" style={{ marginTop: "6rem" }}>
        <div className="container-fluid">
          {isEdit ? (
            <section className="chartBox_area">
              <div className="card">
                <div className="card-header">
                  <h4 className="mb-0">Edit FAQ</h4>
                </div>
                <div className="card-body">
                  <form onSubmit={handleSubmit}>
                    <div className="row">
                      {/* Question Input */}
                      <div className="col-md-12 mt-3">
                        <label htmlFor="question">
                          Question<span className="text-danger">*</span>
                        </label>
                        <input
                          type="text"
                          id="question"
                          className="form-control"
                          value={editData?.question || ""}
                          onChange={(e) =>
                            setEditData((prev) => ({
                              ...prev,
                              question: e.target.value,
                            }))
                          }
                          placeholder="Enter FAQ question..."
                          required
                        />
                        <small className="text-muted">
                          Enter the frequently asked question
                        </small>
                      </div>

                      {/* Answer Textarea */}
                      <div className="col-md-12 mt-3">
                        <label htmlFor="answer">
                          Answer<span className="text-danger">*</span>
                        </label>
                        <textarea
                          id="answer"
                          className="form-control"
                          value={editData?.answer || ""}
                          onChange={(e) =>
                            setEditData((prev) => ({
                              ...prev,
                              answer: e.target.value,
                            }))
                          }
                          rows="6"
                          placeholder="Enter detailed answer..."
                          required
                          style={{ height: "200px" }}
                        ></textarea>
                        <small className="text-muted">
                          Provide a clear and detailed answer
                        </small>
                      </div>
                    </div>

                    <div className="mt-4 pt-3 border-top">
                      <button
                        type="submit"
                        className="btn btn-primary me-3"
                        style={{
                          background: "rgb(133, 218, 79)",
                          border: "none",
                          padding: "10px 25px",
                          fontWeight: "500",
                        }}
                      >
                        <i className="fa-solid fa-floppy-disk me-2"></i>
                        Save Changes
                      </button>
                      <button
                        type="button"
                        onClick={handleCancel}
                        className="btn btn-secondary"
                        style={{
                          padding: "10px 25px",
                          fontWeight: "500",
                        }}
                      >
                        <i className="fa-solid fa-times me-2"></i>
                        Cancel
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </section>
          ) : (
            <>
              <div className="mt-5">
                <div style={headLineStyle} className="page-headline">
                  Manage FAQs
                </div>
                {loading ? (
                  <div className="text-center py-5">
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                    <p className="mt-2">Loading FAQs...</p>
                  </div>
                ) : faqs.length > 0 ? (
                  <div className="card">
                    <div className="card-body">
                      <DataTable
                        columns={columns}
                        data={faqs}
                        pagination
                        responsive
                        highlightOnHover
                        striped
                        paginationPerPage={10}
                        paginationRowsPerPageOptions={[10, 20, 30, 50]}
                        noDataComponent={
                          <div className="text-center py-4">
                            <i className="fa-solid fa-question-circle fa-2x text-muted mb-3"></i>
                            <p>No FAQs found</p>
                          </div>
                        }
                      />
                    </div>
                  </div>
                ) : (
                  <div className="card">
                    <div className="card-body text-center py-5">
                      <i className="fa-solid fa-question-circle fa-3x text-muted mb-3"></i>
                      <h5>No FAQs Available</h5>
                      <p className="text-muted">
                        There are no FAQs to display at the moment.
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Add CSS for the popup */}
      <style>
        {`
          .faq-popup .swal2-popup {
            max-width: 700px;
          }
          .faq-popup .swal2-html-container {
            overflow: hidden;
          }
        `}
      </style>
    </>
  );
}

export default ManageFaq;
